import React, { useEffect, useRef, useState } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import { t } from '@/utils/i18n';
import { AutoplayContent } from './ui/AutoplayContent';
import { templates } from '@/components/feature-configs/autoplay/autoplayTemplates';
import {
  computeAutoplayResponsiveScales,
  DEFAULT_SCALES,
  legacyScaleFromWidthPercent,
  type ResponsiveScales,
} from '@/utils/autoplayResponsiveScale';

// Define quais templates usam quais campos e seus defaults
const TEMPLATE_FIELD_USAGE = {
  'small': { usesTextTop: true, usesTextBottom: false, defaultTop: 'Clique para ouvir', defaultBottom: '' },
  'autoPlay': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Ative o som', defaultBottom: 'Seu video já começou' },
  'large': { usesTextTop: false, usesTextBottom: true, defaultTop: '', defaultBottom: 'Clique para ouvir' },
  'fullScreen': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Seu vídeo já começou', defaultBottom: 'Ative o som' },
  'transparent': { usesTextTop: false, usesTextBottom: true, defaultTop: '', defaultBottom: 'Seu vídeo já começou' },
  'blur': { usesTextTop: true, usesTextBottom: true, defaultTop: 'Conteúdo Sensível', defaultBottom: 'Este vídeo apresenta conteúdo que os grandes produtores não querem que você assista!' },
  'animation': { usesTextTop: true, usesTextBottom: false, defaultTop: 'Seu video já começou', defaultBottom: '' }
};

// Templates que suportam redimensionamento livre.
// `custom` (imagem personalizada) entra aqui para receber o mesmo tratamento de
// posição/tamanho dos demais — antes tinha caixa fixa de 195x110px.
const RESIZABLE_TEMPLATES = ['autoPlay', 'small', 'large', 'transparent', 'custom'];

interface AutoplayOverlayProps {
  features: VideoFeatures;
  onClick: () => void;
  /** Quando true, o overlay usa position:relative (o wrapper externo cuida do posicionamento) */
  isEditing?: boolean;
  /** Quando true, o overlay preenche a altura total do wrapper (usado quando
   *  o usuário definiu uma altura explícita via resize diagonal/vertical). */
  stretchHeight?: boolean;
}

export const AutoplayOverlay: React.FC<AutoplayOverlayProps> = ({ features, onClick, isEditing, stretchHeight }) => {
  // Safely access autoplayOverlay properties with sensible defaults
  const autoplayOverlay = features.autoplayOverlay || {
    colors: { background: 'rgba(0, 170, 255, 0.46)', text: '#FFFFFF', icon: '#FFFFFF' },
    textTop: t('autoplayTextTop'),
    textBottom: t('autoplayTextBottom'),
    whiteBorder: false,
    showBorder: false,
    borderColor: '#FFFFFF',
    borderWidth: 2,
    pulseAnimation: false,
    useTemplate: true,
    customImage: undefined,
    showIcon: true,
    templateId: 'autoPlay'
  };

  // Get current template configuration
  const currentTemplate = templates.find(t => t.id === autoplayOverlay.templateId) || templates[0];
  const templateStyle = currentTemplate.style;
  // `custom` não existe na lista `templates` (é o modo imagem personalizada, não
  // um preset), então o find acima cai no fallback autoPlay. Preservamos o id cru
  // para que o custom seja reconhecido como redimensionável daqui pra frente.
  const templateId =
    autoplayOverlay.templateId === 'custom' ? 'custom' : currentTemplate.id;

  // Trata o default legado do backend (rgba(34, 34, 34, *)) como "sem cor",
  // para que Texto e Ícone caiam no fallback branco em vez de cinza-escuro.
  const isLegacyTextDefault = (val?: string) =>
    !val || /^rgba?\(\s*34\s*,\s*34\s*,\s*34/i.test(val);
  const textColor = isLegacyTextDefault(autoplayOverlay.colors?.text)
    ? '#FFFFFF'
    : autoplayOverlay.colors!.text;
  const iconColor = isLegacyTextDefault(autoplayOverlay.colors?.icon)
    ? '#FFFFFF'
    : autoplayOverlay.colors!.icon;

  // Get defaults for current template
  const fieldUsage = TEMPLATE_FIELD_USAGE[templateId as keyof typeof TEMPLATE_FIELD_USAGE] || TEMPLATE_FIELD_USAGE['autoPlay'];

  // Defaults de posição/tamanho por template (para renderização sem o wrapper de edição)
  const TEMPLATE_LAYOUT_DEFAULTS: Record<string, { xPercent: number; yPercent: number; widthPercent: number }> = {
    autoPlay:    { xPercent: 50, yPercent: 50, widthPercent: 30 },
    small:       { xPercent: 50, yPercent: 12, widthPercent: 28 },
    large:       { xPercent: 50, yPercent: 50, widthPercent: 36 },
    transparent: { xPercent: 50, yPercent: 50, widthPercent: 30 },
    // Equivale à caixa fixa antiga (195px de largura ≈ 30% de um player de 640px).
    custom:      { xPercent: 50, yPercent: 50, widthPercent: 30 },
  };

  // Escalas responsivas (fonte/ícone/gap/padding) baseadas no tamanho FÍSICO
  // em px do overlay, medido via ResizeObserver. Antes do primeiro disparo do
  // observer, usa fallback legado (widthPct / defaultPct) pra evitar flash.
  const savedWidthPct = autoplayOverlay.size?.widthPercent;
  const defaultWidthPct = TEMPLATE_LAYOUT_DEFAULTS[templateId]?.widthPercent ?? 30;
  const overlayRef = useRef<HTMLDivElement>(null);
  const [scales, setScales] = useState<ResponsiveScales>(() => {
    const legacy = legacyScaleFromWidthPercent(savedWidthPct, defaultWidthPct, templateId);
    return {
      fontScale: legacy,
      iconScale: legacy,
      gapScale: legacy,
      paddingScale: 1.0,
    };
  });
  // Orientação do vídeo (vertical = reels). Algumas variações de template
  // (ex.: "blur"/Censurado) usam tamanhos de fonte diferentes por orientação.
  const [isReels, setIsReels] = useState(false);

  useEffect(() => {
    const el = overlayRef.current;
    if (!el) return;

    // Container do player = o ancestral mais próximo que define o "viewport"
    // do overlay. Usado pra detectar breakpoint. Se não achar, cai no parent.
    const findContainer = () =>
      (el.closest('[data-video-container]') as HTMLElement | null) ?? el.parentElement;

    const update = (width: number, height: number) => {
      const container = findContainer();
      const containerWidth = container?.clientWidth ?? width;
      const containerHeight = container?.clientHeight ?? height;
      setIsReels(containerHeight > containerWidth);
      setScales(computeAutoplayResponsiveScales({
        overlayWidthPx: width,
        overlayHeightPx: height,
        containerWidthPx: containerWidth,
        templateId,
      }));
    };

    // Medição inicial síncrona pra primeiro paint já vir certinho.
    const rect = el.getBoundingClientRect();
    if (rect.width > 0) update(rect.width, rect.height);

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      update(width, height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [templateId]);

  // Generate styles based on template type (matching old project exactly)
  const getTemplateStyles = (): React.CSSProperties => {
    const baseColor = autoplayOverlay.colors?.background || currentTemplate.colors.background;

    // Border logic: apenas `whiteBorder` é o toggle do usuário. `showBorder` é
    // campo legado duplicado no load (ambos recebem `haveBorderWhite`), mas só
    // `whiteBorder` é atualizado pelo switch — combinar com OR travava o
    // estado em `true` e quebrava o toggle.
    // Usamos `1px solid transparent` (não `none`) no estado off para evitar
    // layout shift quando o usuário liga/desliga o switch.
    const shouldShowBorder = !!autoplayOverlay.whiteBorder;
    const borderColor = autoplayOverlay.colors?.border && autoplayOverlay.colors.border !== 'rgba(255, 255, 255, 0)'
      ? autoplayOverlay.colors.border
      : '#FFFFFF';
    const hasBorder = shouldShowBorder ? `1px solid ${borderColor}` : '1px solid transparent';

    // O template `custom` (imagem personalizada) não tem mais caixa fixa: entrou
    // em RESIZABLE_TEMPLATES e segue o mesmo caminho de position/size dos demais.
    // O bloco `isCustomImageMode` mais abaixo zera fundo/borda/padding para a
    // imagem ocupar a caixa inteira.

    // Para templates redimensionáveis, aplicar layout customizado quando disponível
    if (RESIZABLE_TEMPLATES.includes(templateId)) {
      const layoutDefaults = TEMPLATE_LAYOUT_DEFAULTS[templateId] || { xPercent: 50, yPercent: 50, widthPercent: 30 };
      const savedPos = autoplayOverlay.position;
      const savedSize = autoplayOverlay.size;

      // Quando isEditing, o wrapper externo cuida da posição; o overlay usa position:relative.
      // Se `stretchHeight` vier true, o wrapper tem altura explícita — o overlay
      // preenche 100% dessa altura. Senão, altura é auto (baseada no conteúdo
      // + minHeight) como antes.
      if (isEditing) {
        const bgColor = templateId === 'transparent' ? 'transparent' : baseColor;
        return {
          position: 'relative',
          width: '100%',
          height: stretchHeight ? '100%' : undefined,
          backgroundColor: bgColor,
          color: textColor,
          border: hasBorder,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: templateId === 'small' ? 'row' : 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'inherit',
          padding: templateId === 'small' ? '8px 12px' : '12px 16px',
          gap: templateId === 'small' ? '5px' : undefined,
          boxSizing: 'border-box',
          minHeight: templateId === 'small' ? '40px' : '60px',
        };
      }

      // Quando não está editando mas tem posição/tamanho salvos — aplica-os
      if (savedPos || savedSize) {
        const xPct = savedPos?.xPercent ?? layoutDefaults.xPercent;
        const yPct = savedPos?.yPercent ?? layoutDefaults.yPercent;
        const wPct = savedSize?.widthPercent ?? layoutDefaults.widthPercent;
        const bgColor = templateId === 'transparent' ? 'transparent' : baseColor;

        return {
          position: 'absolute',
          top: `${yPct}%`,
          left: `${xPct}%`,
          transform: 'translate(-50%, -50%)',
          width: `${wPct}%`,
          backgroundColor: bgColor,
          color: textColor,
          border: hasBorder,
          borderRadius: '10px',
          display: 'flex',
          flexDirection: templateId === 'small' ? 'row' : 'column',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          padding: templateId === 'small' ? '8px 12px' : '12px 16px',
          gap: templateId === 'small' ? '5px' : undefined,
          boxSizing: 'border-box',
          minHeight: templateId === 'small' ? '40px' : '60px',
          zIndex: 20,
        };
      }
    }

    // Template: autoPlay (padrão) — layout legado
    if (templateId === 'autoPlay') {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: baseColor,
        color: textColor,
        border: hasBorder,
        borderRadius: '10px',
        height: '115px',
        width: '210px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 20,
      };
    }

    // Template: small (pequeno) — layout legado
    if (templateId === 'small') {
      return {
        position: 'absolute',
        top: '5%',
        left: '5%',
        backgroundColor: baseColor,
        border: hasBorder,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '40px',
        padding: '0 10px',
        zIndex: 15,
        gap: '5px',
        cursor: 'pointer',
        boxSizing: 'border-box',
      };
    }

    // Template: large (grande) — layout legado
    if (templateId === 'large') {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: baseColor,
        color: textColor,
        border: hasBorder,
        borderRadius: '10px',
        height: '130px',
        width: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 20,
      };
    }

    // Template: fullScreen (tela cheia)
    if (templateId === 'fullScreen') {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: baseColor,
        color: textColor,
        border: hasBorder,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 10,
      };
    }

    // Template: transparent (transparente) — layout legado
    if (templateId === 'transparent') {
      return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'transparent',
        color: textColor,
        border: hasBorder,
        borderRadius: '10px',
        height: '100px',
        width: '200px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 20,
      };
    }

    // Template: blur (censurado)
    if (templateId === 'blur') {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: baseColor,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: textColor,
        border: hasBorder,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 10,
      };
    }

    // Template: animation (animado)
    if (templateId === 'animation') {
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: baseColor,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        color: textColor,
        border: hasBorder,
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        zIndex: 10,
      };
    }

    // Default fallback
    return {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: baseColor,
      color: textColor,
      border: hasBorder,
      borderRadius: '10px',
      height: '115px',
      width: '210px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      zIndex: 20,
    };
  };

  let autoplayOverlayStyle = getTemplateStyles();

  const isCustomImageMode =
    !!autoplayOverlay.customImage && !autoplayOverlay.useTemplate;

  if (isCustomImageMode) {
    autoplayOverlayStyle = {
      ...autoplayOverlayStyle,
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: undefined,
      padding: 0,
      boxShadow: 'none',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      minHeight: undefined,
    };
  }

  // Pulso só quando o usuário ativou explicitamente — nunca pelo default do template.
  // Lê só `pulseAnimation` (primário). `pulseEffect` é legado e é duplicado no
  // load, mas só `pulseAnimation` é atualizado pelo switch; combinar com OR
  // trava em `true` e quebra o toggle.
  const shouldPulse = !!autoplayOverlay.pulseAnimation;

  if (shouldPulse && !isEditing) {
    // Para isEditing=true, o pulso é aplicado no wrapper (AutoplayResizeWrapper) que tem o translate
    if (RESIZABLE_TEMPLATES.includes(templateId) && (autoplayOverlay.position || autoplayOverlay.size)) {
      // Template redimensionável com posição salva: mantém o translate-50%/-50% do layout customizado
      // Precisa vir ANTES do check de 'small' para não usar pulseSmallPlay quando há translate
      autoplayOverlayStyle = {
        ...autoplayOverlayStyle,
        animation: 'pulseScale 2500ms ease-in-out infinite'
      };
    } else if (templateId === 'small') {
      // Small sem posição salva (layout legado sem translate): scale puro
      autoplayOverlayStyle = {
        ...autoplayOverlayStyle,
        animation: 'pulseSmallPlay 2500ms ease-in-out infinite'
      };
    } else if (templateId === 'fullScreen' || templateId === 'blur' || templateId === 'animation') {
      autoplayOverlayStyle = {
        ...autoplayOverlayStyle,
        animation: 'pulse 2500ms ease-in-out infinite'
      };
    } else {
      // Templates centralizados com posição legada (translate -50%/-50% no inline style)
      autoplayOverlayStyle = {
        ...autoplayOverlayStyle,
        animation: 'pulsePlay 2500ms ease-in-out infinite'
      };
    }
  }

  return (
    <>
      <style>{`
        @keyframes pulseSmallPlay {
          0%   { transform: scale(1);   opacity: 1; }
          50%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }

        @keyframes pulsePlay {
          0%   { transform: translate(-50%, -50%) scale(1);   }
          50%  { transform: translate(-50%, -50%) scale(1.2); }
          100% { transform: translate(-50%, -50%) scale(1);   }
        }

        @keyframes pulseScale {
          0%   { transform: translate(-50%, -50%) scale(1);   }
          50%  { transform: translate(-50%, -50%) scale(1.1); }
          100% { transform: translate(-50%, -50%) scale(1);   }
        }

        @keyframes pulse {
          0%   { transform: scale(1);    }
          50%  { transform: scale(1.05); }
          100% { transform: scale(1);    }
        }
      `}</style>
      <div
        ref={overlayRef}
        className="autoplay-overlay"
        style={autoplayOverlayStyle}
        onClick={isEditing ? undefined : onClick}
      >
        <AutoplayContent
          textTop={autoplayOverlay.textTop || fieldUsage.defaultTop}
          textBottom={autoplayOverlay.textBottom || fieldUsage.defaultBottom}
          textColor={textColor}
          iconColor={iconColor}
          customImage={autoplayOverlay.customImage}
          useTemplate={autoplayOverlay.useTemplate}
          showIcon={autoplayOverlay.showIcon !== false}
          templateId={templateId}
          scales={scales}
          isReels={isReels}
        />
      </div>
    </>
  );
};
